import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { usePageMeta } from '../lib/usePageMeta'

// Full vessel shape (fetched from /data/vessels/<slug>.json, written by prerender.vessels.mjs). Every
// field is data-gated: a missing spec drops its row rather than showing a blank or a guess.
interface Vessel {
  slug: string
  imo: string
  name: string
  flag: string | null
  call_sign: string | null
  mmsi: string | null
  ship_type: string | null
  teu: number | null
  length_m: number | null
  beam_m: number | null
  draught_m: number | null
  year_built: number | null
  deadweight: number | null
  gross_tonnage: number | null
  class_society: string | null
  nav_status: string | null
  destination_name: string | null
  eta_ais: string | null
  position_updated_at: string | null
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`
}
const num = (v: number | null | undefined) => (v == null ? null : v.toLocaleString('en-US'))

function typeLabel(v: Vessel): string {
  const t = (v.ship_type || '').toLowerCase()
  if (t.includes('container')) return 'container ship'
  if (t) return v.ship_type?.toLowerCase() ?? 'cargo vessel'
  return 'cargo vessel'
}

export function VesselPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>()
  const slug = rawSlug?.replace(/\.html$/i, '').replace(/\/+$/, '')
  const [v, setV] = useState<Vessel | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'notfound'>('loading')

  useEffect(() => {
    if (!slug) return
    setState('loading')
    fetch(`/data/vessels/${slug}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: Vessel) => {
        setV(data)
        setState('ready')
      })
      .catch(() => setState('notfound'))
  }, [slug])

  usePageMeta(
    v
      ? `${v.name} (IMO ${v.imo}): ${v.ship_type || 'container ship'} specs & position | SchedulesMCP`
      : 'Vessel · SchedulesMCP',
    v
      ? `${v.name}: ${v.teu != null ? `${num(v.teu)} TEU, ` : ''}${v.length_m ? `${num(v.length_m)} m LOA, ` : ''}${v.year_built ? `built ${v.year_built}. ` : ''}Particulars and last-known position.`
      : 'Vessel particulars and tracking.'
  )

  if (state === 'notfound')
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <h1
          className="font-display text-3xl mb-3"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Vessel not found
        </h1>
        <Link to="/vessels" className="text-signal no-underline hover:underline">
          See all vessels →
        </Link>
      </main>
    )
  if (state === 'loading' || !v)
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16 text-[var(--text-muted)]">Loading…</main>
    )

  const specs: [string, string | number | null][] = [
    ['IMO number', v.imo],
    ['MMSI', v.mmsi],
    ['Call sign', v.call_sign],
    ['Vessel type', v.ship_type],
    ['Capacity (TEU)', v.teu != null ? num(v.teu) : null],
    ['Length overall', v.length_m != null ? `${num(v.length_m)} m` : null],
    ['Beam', v.beam_m != null ? `${num(v.beam_m)} m` : null],
    ['Draught', v.draught_m != null ? `${num(v.draught_m)} m` : null],
    ['Deadweight', v.deadweight != null ? `${num(v.deadweight)} t` : null],
    ['Gross tonnage', v.gross_tonnage != null ? `${num(v.gross_tonnage)} GT` : null],
    ['Year built', v.year_built],
    ['Flag', v.flag],
    ['Class society', v.class_society],
  ]
  const rows = specs.filter(([, val]) => val != null && val !== '')

  const sizeBits: string[] = []
  if (v.teu != null) sizeBits.push(`${num(v.teu)}-TEU`)
  const dims: string[] = []
  if (v.length_m != null) dims.push(`${num(v.length_m)} m long`)
  if (v.beam_m != null) dims.push(`${num(v.beam_m)} m in beam`)

  return (
    <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
      <nav className="text-sm text-[var(--text-muted)] mb-5">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        ›{' '}
        <Link to="/vessels" className="no-underline hover:underline">
          Vessels
        </Link>{' '}
        › {v.name}
      </nav>
      <p
        className="m-0 font-mono text-xs uppercase text-accent"
        style={{ letterSpacing: '0.14em' }}
      >
        IMO {v.imo}
        {v.ship_type ? ` · ${v.ship_type}` : ''}
      </p>
      <h1
        className="font-display text-4xl mt-3 mb-4"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {v.name}: vessel particulars & position
      </h1>
      <p className="text-[var(--text-primary)] text-lg font-medium mb-2 max-w-2xl leading-relaxed">
        {v.name} (IMO {v.imo}) is a {sizeBits.length ? `${sizeBits.join(' ')} ` : ''}
        {typeLabel(v)}
        {v.year_built ? ` built in ${v.year_built}` : ''}
        {v.flag ? `, registered under the ${v.flag} flag` : ''}.
        {dims.length ? ` She measures ${dims.join(' and ')}.` : ''}
      </p>

      <h2 className="font-display font-light tracking-[-0.015em] text-xl mt-10 mb-3">
        {v.name} specifications
      </h2>
      <div className="overflow-x-auto border border-border rounded-xl">
        <table className="w-full border-collapse text-sm">
          <tbody>
            {rows.map(([k, val]) => (
              <tr key={k} className="border-b border-border">
                <td className="p-3 text-[var(--text-muted)]">{k}</td>
                <td className="p-3 text-[var(--text-primary)] font-mono">{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {v.position_updated_at && (
        <>
          <h2 className="font-display font-light tracking-[-0.015em] text-xl mt-10 mb-3">
            Where is {v.name} now?
          </h2>
          <p className="text-[var(--text-primary)] leading-relaxed max-w-2xl mb-4">
            As of {fmtDate(v.position_updated_at)}, {v.name} was last tracked
            {v.nav_status ? `, status ${v.nav_status.toLowerCase()}` : ''}
            {v.destination_name ? `, reported bound for ${v.destination_name}` : ''}
            {v.eta_ais ? `, with an AIS ETA of ${fmtDate(v.eta_ais)}` : ''}. Positions update
            continuously. Open the live tracker for {v.name}'s current location.
          </p>
          <a
            href="https://trackingmcp.com"
            className="inline-block bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
          >
            Track {v.name} live
          </a>
        </>
      )}

      <div className="mt-12">
        <Link
          to="/vessels"
          className="inline-flex items-center gap-1.5 text-signal no-underline hover:underline"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            aria-hidden
            role="presentation"
            className="w-4 h-4"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          All vessels
        </Link>
      </div>
    </main>
  )
}
